import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { planService, CreatePlanData } from '../../../services/plan.service';
import { toast } from 'react-hot-toast';

export default function PlanForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreatePlanData>({
    name: '',
    displayName: '',
    description: '',
    cost: 0,
    currency: 'CAD',
    duration: 'monthly',
    maxUsers: 5,
    maxClients: 10,
    features: [],
    sequenceNumber: 1,
    featureFlags: {
      eSign: false,
      payroll: false,
      biometricImport: false,
      advancedReporting: false,
      apiAccess: false,
      multiCurrency: false,
      customBranding: false,
      prioritySupport: false,
    },
  });

  const [newFeature, setNewFeature] = useState({ name: '', description: '', enabled: true });

  useEffect(() => {
    if (isEditMode) {
      loadPlan();
    }
  }, [id]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const plan = await planService.getPlanById(id!);
      setFormData({
        name: plan.name,
        displayName: plan.displayName,
        description: plan.description,
        cost: plan.cost,
        currency: plan.currency,
        duration: plan.duration,
        maxUsers: plan.maxUsers,
        maxClients: plan.maxClients,
        features: plan.features,
        sequenceNumber: plan.sequenceNumber,
        featureFlags: plan.featureFlags,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load plan');
      navigate('/platform/plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      if (isEditMode) {
        await planService.updatePlan(id!, formData);
        toast.success('Plan updated successfully');
      } else {
        await planService.createPlan(formData);
        toast.success('Plan created successfully');
      }
      navigate('/platform/plans');
    } catch (error: any) {
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} plan`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.name.trim()) {
      toast.error('Feature name is required');
      return;
    }

    setFormData({
      ...formData,
      features: [...(formData.features || []), { ...newFeature }],
    });
    setNewFeature({ name: '', description: '', enabled: true });
  };

  const handleRemoveFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features?.filter((_, i) => i !== index) || [],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Plan' : 'Create New Plan'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditMode ? 'Update plan details' : 'Define a new subscription plan'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Plan Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., starter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Display Name *
              </label>
              <input
                type="text"
                required
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., Starter Plan"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description *
              </label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Describe the plan..."
              />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Pricing</h2>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Cost *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Currency *
              </label>
              <select
                required
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Duration *
              </label>
              <select
                required
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </div>
          </div>
        </div>

        {/* Limits */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Limits</h2>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Max Users *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.maxUsers}
                onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Max Clients *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.maxClients}
                onChange={(e) => setFormData({ ...formData, maxClients: parseInt(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Sequence Number *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.sequenceNumber}
                onChange={(e) => setFormData({ ...formData, sequenceNumber: parseInt(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Feature Flags */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Feature Flags</h2>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Object.entries(formData.featureFlags || {}).map(([key, value]) => (
              <label key={key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      featureFlags: {
                        ...formData.featureFlags,
                        [key]: e.target.checked,
                      },
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Custom Features */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Custom Features</h2>
          
          {/* Add Feature */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <div className="sm:col-span-4">
                <input
                  type="text"
                  value={newFeature.name}
                  onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Feature name"
                />
              </div>
              <div className="sm:col-span-6">
                <input
                  type="text"
                  value={newFeature.description}
                  onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Description (optional)"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={handleAddFeature}
                  className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Feature List */}
            {formData.features && formData.features.length > 0 && (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                {formData.features.map((feature, index) => (
                  <li key={index} className="px-4 py-3 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{feature.name}</p>
                      {feature.description && (
                        <p className="text-sm text-gray-500">{feature.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="ml-4 text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/platform/plans')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : isEditMode ? 'Update Plan' : 'Create Plan'}
          </button>
        </div>
      </form>
    </div>
  );
}
